import { CSRF_HEADER } from "@lms/backend/lib/csrf-constants";
import { getStoredAccessToken } from "@/lib/auth-token-storage";
import { apiUrl } from "@/services/http/api-url";
import { httpClient } from "@/services/http/client";
import { peekCsrfToken } from "@/services/http/csrf-token";

export type PlayerLesson = {
  id: string;
  title: string;
  subtitle: string | null;
  displayTitle: string;
  contentType: string;
  videoProvider?: string;
  hasVideo: boolean;
  hasText: boolean;
  /** R-3: holds the course until its video is ready, or doesn't count toward finishing it. */
  completionState?: "completable" | "awaiting_video" | "not_counted";
  isLocked: boolean;
  unlockAt: string | null;
  /** LRN-10 / LRN-21: the signed-in user's own progress (null = not started). */
  progress?: PlayerLessonProgress | null;
};

export type PlayerLessonProgress = {
  completed: boolean;
  watchedSeconds: number;
  positionSeconds: number;
  lastViewedAt: string | null;
};

export type PlayerResume = {
  lessonId: string;
  positionSeconds: number;
  reason: "last_viewed" | "first_incomplete" | "first";
};

export type PlayerModule = {
  id: string;
  title: string;
  sortOrder: number;
  lessons: PlayerLesson[];
};

export type PlayerOutline = {
  course: { id: string; slug: string; title: string };
  modules: PlayerModule[];
  cohortName?: string | null;
  /** LRN-10: where "Continue learning" lands (null when no lesson is open yet). */
  resume?: PlayerResume | null;
};

export type LessonContent = {
  bodyHtml: string | null;
  bodyHtmlHi: string | null;
  attachments: Array<{ id: string; fileName: string }>;
};

export async function fetchPlayerOutline(courseSlug: string): Promise<PlayerOutline> {
  const res = await httpClient.get<{ data: PlayerOutline }>(`/v1/learn/${courseSlug}/outline`);
  return res.data.data;
}

export async function fetchLessonContent(lessonId: string): Promise<LessonContent> {
  const res = await httpClient.get<{ data: LessonContent }>(`/v1/lessons/${lessonId}/content`);
  return res.data.data;
}

export type PlaybackSource =
  { kind: "local"; url: string } | { kind: "bunny"; embedUrl: string; expiresAt: string };

/** INFRA-007: a local stream URL, or a signed Bunny embed minted after the entitlement check. */
export async function fetchPlaybackSource(lessonId: string): Promise<PlaybackSource> {
  const res = await httpClient.get<{ data: PlaybackSource }>(
    `/v1/lessons/${lessonId}/playback-source`,
  );
  return res.data.data;
}

export type LessonProgressResult = {
  lessonId: string;
  watchedSeconds: number;
  completed: boolean;
  positionSeconds?: number;
};

export type LessonProgressBody = {
  watchedSeconds?: number;
  completed?: boolean;
  durationSec?: number;
  positionSeconds?: number;
};

export async function postLessonProgress(
  lessonId: string,
  body: LessonProgressBody,
): Promise<LessonProgressResult> {
  const res = await httpClient.post<{ data: LessonProgressResult }>(
    `/v1/lessons/${lessonId}/progress`,
    body,
  );
  return res.data.data;
}

/**
 * LRN-10: the last position as the page goes away (tab closed, lesson left). A keepalive fetch
 * outlives the page and — unlike sendBeacon — carries the Bearer token and the CSRF header. Uses
 * the CSRF token already in hand; returns false when there is none (nothing is sent).
 */
export function sendLessonProgressOnExit(lessonId: string, body: LessonProgressBody): boolean {
  const csrf = peekCsrfToken();
  if (!csrf) return false;
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    [CSRF_HEADER]: csrf,
  };
  const token = getStoredAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  try {
    void fetch(apiUrl(`/v1/lessons/${lessonId}/progress`), {
      method: "POST",
      keepalive: true,
      credentials: "include",
      headers,
      body: JSON.stringify(body),
    }).catch(() => undefined);
    return true;
  } catch {
    return false;
  }
}
